// src/components/LayerPanel/CountyThumbnail.jsx
//
// SVG thumbnail for one county card in the coverage flyout. Three layers:
//   1. County outline                 (stroke, no fill)
//   2. AOI clipped to this county     (stroke, no fill — thicker)
//   3. Coverage overlay               (solid if "full" bucket, hatched if
//                                      "partial"/"minimal", omitted when 0)
//
// The "partial" case is a small lie: we don't have the exact geometry of the
// imagery∩aoi∩county intersection at the series level (it would require a
// geometric union across acquisitions; the rollup carries only ratios). So
// we use the AOI-within-county shape as the hatching boundary and let the
// ratio number on the card carry the precise value. Accurate enough for a
// 140px thumbnail; the chevron on the row title still drills down to per-
// acquisition pills for exact dates.
//
// Pure rendering — no state, no fetches. Uses a stable per-instance pattern
// id so multiple thumbnails on screen don't share/collide hatch defs.

import { useId } from "react";

const DEFAULT_SIZE = 140;
const PADDING_RATIO = 0.06; // 6% inset so strokes don't clip at the edges

/**
 * @param {object} props
 * @param {object} props.countyGeom        GeoJSON Polygon/MultiPolygon (required)
 * @param {object} props.aoiCountyGeom     GeoJSON Polygon/MultiPolygon (required)
 * @param {number|null} props.coverageRatio  aoi_coverage_ratio for this county.
 *                                           Drives the overlay treatment.
 * @param {number} [props.size]            SVG width/height in px (square)
 */
export default function CountyThumbnail({
  countyGeom,
  aoiCountyGeom,
  coverageRatio,
  size = DEFAULT_SIZE,
}) {
  const patternId = useId();

  if (!countyGeom || !aoiCountyGeom) return null;

  const bbox = computeBbox(countyGeom);
  if (!bbox) return null;

  const project = makeProjector(bbox, size);
  const countyPath = geomToPath(countyGeom, project);
  const aoiPath = geomToPath(aoiCountyGeom, project);
  if (!countyPath || !aoiPath) return null;

  // Display bucket — same thresholds as coverageBuckets.js. Inlined the
  // numbers rather than importing because the thumbnail's overlay treatment
  // is its own concern: "draw it solid" / "draw it hatched" / "draw nothing"
  // is a rendering decision, not a counting one.
  const r = coverageRatio ?? 0;
  const overlay = r >= 0.95 ? "solid" : r >= 0.1 ? "hatched" : r > 0 ? "minimal-hatched" : "none";

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      role="img"
      aria-label="County coverage thumbnail"
      className="block"
    >
      <defs>
        <pattern
          id={patternId}
          patternUnits="userSpaceOnUse"
          width="6"
          height="6"
          patternTransform="rotate(45)"
        >
          {/* Hatch lines — color via stroke; opacity tuned per-overlay below */}
          <line x1="0" y1="0" x2="0" y2="6" stroke="currentColor" strokeWidth="1.5" />
        </pattern>
      </defs>

      {/* Card background — subtle so the geometry pops */}
      <rect x="0" y="0" width={size} height={size} className="fill-slate-900" />

      {/* 1. County outline */}
      <path
        d={countyPath}
        className="fill-none stroke-slate-600"
        strokeWidth="1"
      />

      {/* 3. Coverage overlay — drawn beneath the AOI outline so the outline
            stays crisp on top of the fill */}
      {overlay === "solid" && (
        <path
          d={aoiPath}
          className="fill-accent-info/40 stroke-none"
        />
      )}
      {overlay === "hatched" && (
        <path
          d={aoiPath}
          fill={`url(#${patternId})`}
          className="text-accent-info/60 stroke-none"
        />
      )}
      {overlay === "minimal-hatched" && (
        <path
          d={aoiPath}
          fill={`url(#${patternId})`}
          className="text-accent-info/30 stroke-none"
        />
      )}

      {/* 2. AOI-within-county outline (drawn last so it sits on top) */}
      <path
        d={aoiPath}
        className="fill-none stroke-accent-info"
        strokeWidth="1.5"
      />
    </svg>
  );
}

// ───────────────────────── geometry helpers ─────────────────────────

/**
 * Walk a GeoJSON Polygon or MultiPolygon and call `fn` on every [lng, lat]
 * pair. No-op for unsupported types.
 */
function forEachCoord(geom, fn) {
  if (!geom?.coordinates) return;
  if (geom.type === "Polygon") {
    for (const ring of geom.coordinates) for (const p of ring) fn(p);
  } else if (geom.type === "MultiPolygon") {
    for (const poly of geom.coordinates)
      for (const ring of poly) for (const p of ring) fn(p);
  }
}

function computeBbox(geom) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  forEachCoord(geom, ([x, y]) => {
    if (typeof x !== "number" || typeof y !== "number") return;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  });
  if (!isFinite(minX)) return null;
  return { minX, minY, maxX, maxY };
}

/**
 * Build a projector from lng/lat → SVG x/y. Preserves aspect ratio by using
 * the larger axis to set the scale, then centering on the unused dimension.
 * Y is flipped (SVG y grows downward; lat grows upward).
 */
function makeProjector(bbox, size) {
  const inset = size * PADDING_RATIO;
  const drawable = size - inset * 2;

  const w = bbox.maxX - bbox.minX || 1e-9;
  const h = bbox.maxY - bbox.minY || 1e-9;
  const scale = Math.min(drawable / w, drawable / h);

  const offsetX = (size - w * scale) / 2;
  const offsetY = (size - h * scale) / 2;

  return ([lng, lat]) => {
    const x = (lng - bbox.minX) * scale + offsetX;
    const y = size - ((lat - bbox.minY) * scale + offsetY); // flip y
    return [x, y];
  };
}

/**
 * Convert a Polygon/MultiPolygon to an SVG path string. Uses fill-rule
 * "evenodd" implicitly via the M…Z pattern per ring.
 */
function geomToPath(geom, project) {
  if (!geom?.coordinates) return null;

  const ringToPath = (ring) => {
    if (!ring?.length) return "";
    const parts = [];
    ring.forEach(([lng, lat], i) => {
      const [x, y] = project([lng, lat]);
      parts.push(`${i === 0 ? "M" : "L"}${x.toFixed(2)} ${y.toFixed(2)}`);
    });
    parts.push("Z");
    return parts.join(" ");
  };

  if (geom.type === "Polygon") {
    return geom.coordinates.map(ringToPath).join(" ");
  }
  if (geom.type === "MultiPolygon") {
    return geom.coordinates
      .map((poly) => poly.map(ringToPath).join(" "))
      .join(" ");
  }
  return null;
}
